import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import {
  LuClipboardList,
  LuMove,
  LuRotateCw,
  LuScale,
  LuTarget,
  LuSparkles,
  LuActivity,
  LuFileText,
  LuGift,
  LuUsers,
  LuBug,
  LuZap,
} from 'react-icons/lu'

/**
 * "Nouveautés" (what's new) modal for the blot analysis module.
 *
 * CUMULATIVE changelog — releases are never rewritten, only prepended. Each has a
 * `version` (ISO date, so plain string comparison orders them) and a list of
 * operator-facing items. The modal shows the whole recent history and flags every
 * release the current device hasn't acknowledged yet with a "Nouveau" badge, so a
 * user who skipped a version still sees those items on their next visit — nothing
 * is lost. Per-device (localStorage), no backend.
 *
 * ── To ship the next update ──────────────────────────────────────────────────
 * PREPEND a new block at the top of RELEASES:
 *     { version: '2026-08-12', date: '12 août 2026', items: [ … ] }
 * Never edit or delete the older blocks. That's the whole workflow — the latest
 * PUBLISHED version drives the auto-open, and a block dated in the future stays
 * hidden until that day (see visibleReleases), so an update can be staged ahead.
 * Trim to the last ~6 releases when the list gets long.
 */

type Change = { icon: any; title: string; body: string }
type Release = { version: string; date: string; items: Change[] }

// Newest first. Une entrée peut porter une date FUTURE : elle reste invisible
// (modal, auto-ouverture, badge « Nouveau ») tant que cette date n'est pas
// atteinte sur le poste du lecteur — on peut donc déposer la nouveauté le
// vendredi et la faire paraître le lundi, sans redéployer.
const RELEASES: Release[] = [
  {
    version: '2026-11-02',
    date: '2 novembre 2026',
    items: [
      {
        icon: LuActivity,
        title: 'Détection affinée — Toxo IgG et IgM',
        body: 'Fini les fausses bandes dues aux ombres de membrane et aux bords : le bruit de fond est soustrait avant la recherche des pics, sur toutes les recherches. Les vraies bandes, même faibles, restent détectées.',
      },
      {
        icon: LuZap,
        title: 'Analyses 30 % plus rapides',
        body: 'Moteur de détection toujours chaud, lecture des identifiants en parallèle : vos plaques s’analysent environ 30 % plus vite.',
      },
      {
        icon: LuTarget,
        title: 'Les cas à vérifier, mis en évidence',
        body: 'IRIS attire votre attention sur les bandelettes qui méritent un second regard : quand les critères sont tout juste frôlés, la carte s’illumine « À vérifier » — de la galerie au rapport PDF — pour concentrer votre lecture là où elle compte.',
      },
    ],
  },
  {
    version: '2026-10-29',
    date: '29 octobre 2026',
    items: [
      {
        icon: LuScale,
        title: 'Mode rétrospectif : votre lecture face à IRIS',
        body: 'Au dépôt, un cas peut passer en mode rétrospectif : vous notez votre lecture de paillasse en aveugle — images voilées, IRIS masqué — puis le verrouillage dévoile IRIS et chiffre l’accord : conclusions, bande à bande, kappa avec son intervalle.',
      },
      {
        icon: LuClipboardList,
        title: 'Registre : tous vos cas, et l’accord agrégé',
        body: 'Un nouvel onglet dans la barre latérale rassemble tous les cas avec leurs filtres — statut, période, mode et désormais pathologie — et agrège l’accord des lectures verrouillées, pathologie par pathologie. Chaque ligne se déplie sur ses cas, qui s’ouvrent d’un clic.',
      },
      {
        icon: LuBug,
        title: 'Corrections',
        body: 'La ligne de collage est trouvée plus sûrement sur les feuilles courtes (deux bandelettes). Les plaques Dynablot sont rangées sous les recherches qu’elles contiennent réellement. L’accueil suit vos modules : actions rapides, statistiques et fil d’activité ne montrent que ce qui vous est ouvert.',
      },
    ],
  },
  {
    version: '2026-10-23',
    date: '23 octobre 2026',
    items: [
      {
        icon: LuTarget,
        title: 'Amélioration de la ligne de contrôle',
        body: 'La ligne de contrôle est repérée plus fidèlement sur chaque bandelette : beaucoup moins de repositionnement à la main.',
      },
      {
        icon: LuSparkles,
        title: 'Intégration de Trichinella ES et Cysticercose',
        body: 'Les deux tests sont disponibles dans le catalogue, avec leurs critères d’interprétation et la suggestion automatique des bandes.',
      },
      {
        icon: LuRotateCw,
        title: 'Relance de l’analyse',
        body: 'Le bouton de relance demande confirmation et rappelle ce qu’elle efface. Il propose aussi de récupérer les bandelettes supprimées, sans tout relancer.',
      },
      {
        icon: LuBug,
        title: 'Corrections de bugs',
        body: 'Affichage de l’image brute : les boîtes sont bien placées dès le premier affichage et un rapport multi-pages s’affiche en entier, sans avoir à rafraîchir. Une jauge indique l’avancement pendant l’analyse.',
      },
    ],
  },
  {
    version: '2026-10-21',
    date: '21 octobre 2026',
    items: [
      {
        icon: LuSparkles,
        title: 'Dynablot : identifiant échantillon et numéro de lot',
        body: "L'identifiant de l'échantillon et le lot de réactifs sont lus directement dans le tableau du rapport et affichés sur chaque bandelette. Ils sont repris dans tous les exports, et le numéro de lot pré-remplit le formulaire d'export en suivant la pathologie sélectionnée.",
      },
      {
        icon: LuFileText,
        title: 'Rapports plus complets',
        body: "Les écarts de la comparaison mère-enfant apparaissent maintenant dans le rapport PDF, sur l'image comme dans la liste des bandes — ils en étaient absents jusqu'ici. Et le bouton d'export signale par une animation le moment où tout est validé.",
      },
    ],
  },
  {
    version: '2026-10-20',
    date: '20 octobre 2026',
    items: [
      {
        icon: LuTarget,
        title: 'Détection des identifiants améliorée',
        body: "La lecture automatique des identifiants de bandelettes a été fiabilisée : un même identifiant ne peut plus apparaître deux fois sur une plaque, et une lecture douteuse est signalée à confirmer.",
      },
      {
        icon: LuRotateCw,
        title: 'Mère et enfant (CIP-WB)',
        body: "Chaque bandelette appariée affiche son rôle sur sa carte : M pour la mère, E pour l'enfant. Si l'appariement automatique est inversé, le bouton « Inverser M/E » dans la comparaison — ou un clic sur le badge — échange les deux rôles. Le choix est enregistré sur le cas et suivi partout : écarts en kDa, verdict et exports.",
      },
      {
        icon: LuFileText,
        title: 'Rapports et exports plus complets',
        body: "Le rapport PDF contient désormais systématiquement les critères d'interprétation du kit et le verdict de chaque bandelette, suivi de « non validé » lorsque la validation n'a pas été faite. Les exports CSV, JSON et FHIR précisent en plus, pour chaque bandelette, la recherche concernée sur une plaque multitest et le rôle mère/enfant.",
      },
    ],
  },
  {
    version: '2026-10-04',
    date: '4 octobre 2026',
    items: [
      {
        icon: LuBug,
        title: 'Signaler un bug depuis IRIS',
        body: "Dans le bouton « Astuces d'édition » (ampoule), l'option « Signaler un bug » ouvre un formulaire libre. Le signalement part directement à l'équipe, avec le cas en cours et votre navigateur joints, pour que nous puissions reproduire le problème.",
      },
      {
        icon: LuUsers,
        title: 'Module collaboratif',
        body: "Depuis un cas, « Partager le cas » permet maintenant d'inviter des collaborateurs. Chaque collaborateur crée son compte, puis voit le cas partagé et peut l'utiliser comme vous : annoter, valider les bandelettes et exporter le rapport.",
      },
      {
        icon: LuSparkles,
        title: 'Rôle au laboratoire et liste de vos collaborateurs',
        body: "À l'invitation, vous indiquez le rôle de la personne au laboratoire (technicien·ne, interne, biologiste…). Vos collaborateurs actifs sont réutilisables en un clic pour un autre cas, et la liste complète s'affiche dans Mes infos.",
      },
    ],
  },
  {
    version: '2026-09-24',
    date: '24 septembre 2026',
    items: [
      {
        icon: LuSparkles,
        title: 'Dynablot Multitest intégré',
        body: "Le profil Dynablot (plaque multi-pathologies : Toxo IgG/IgM, Schisto, Échinococcus, Toxocara, Aspergillus, Leishmaniose…) est reconnu automatiquement. Chaque bandelette est interprétée avec la config de SA propre pathologie.",
      },
      {
        icon: LuMove,
        title: 'Ligne de contrôle corrigible depuis la modale de comparaison',
        body: "La ligne de contrôle du fabricant (repère jaune) s'affiche directement sur les images dans la modale de comparaison, et peut être corrigée à la main par glisser-déposer si la détection automatique est décalée.",
      },
    ],
  },
  {
    version: '2026-08-13',
    date: '13 août 2026',
    items: [
      {
        icon: LuTarget,
        title: 'Détection des identifiants améliorée',
        body: 'La lecture automatique des identifiants de bandelettes a été fiabilisée.',
      },
      {
        icon: LuActivity,
        title: 'Détection des bandes plus sensible (Toxo IgG et IgM)',
        body: 'Sur les Toxo IgG et IgM, les bandes du patient sont mieux repérées — y compris les plus faibles — et les bandes voisines ne sont plus confondues.',
      },
      {
        icon: LuSparkles,
        title: 'Code couleur unifié des bandes',
        body: '🟢 témoin · 🟣 détectée automatiquement · 🔵 ajoutée à la main — partout (galerie, image, graphique, rapport PDF).',
      },
      {
        icon: LuFileText,
        title: 'Export plus clair',
        body: "Le bouton reste inactif tant que tout n'est pas prêt (bandelettes validées + identifiants complétés), avec un message indiquant ce qu'il reste à faire. Un badge « N° manquant » pointe les bandelettes à compléter.",
      },
      {
        icon: LuGift,
        title: 'Échinococcus — bandes utiles uniquement',
        body: "Seules les bandes utiles au diagnostic et au typage d'espèce sont conservées.",
      },
    ],
  },
  {
    version: '2026-07-28',
    date: '28 juillet 2026',
    items: [
      {
        icon: LuTarget,
        title: 'Reconnaissance des identifiants (Bêta)',
        body: "Le module lit l'identifiant sous la ligne de contrôle (ex. « K10 ») et l'affiche à la place de « Blot 1, 2… » partout. Cliquez pour corriger — vérifiez toujours la lecture.",
      },
      {
        icon: LuMove,
        title: 'Complétion et relecture',
        body: "Saisissez une lettre (« K ») : les bandelettes non lues l'adoptent, reste à mettre le numéro. Une box mal cadrée ? Déplacez-la, l'identifiant est relu (vos saisies restent intactes).",
      },
      {
        icon: LuActivity,
        title: 'Pastilles de confiance',
        body: "🟢 lu (fiable) · ⚪ suggéré (à compléter) · 🔵 saisi à la main · 🟠 à vérifier. L'export est bloqué tant qu'un identifiant manque.",
      },
    ],
  },
  {
    version: '2026-07-27',
    date: '27 juillet 2026',
    items: [
      {
        icon: LuSparkles,
        title: 'Le module s’appelle désormais IRIS',
        body: "L'analyse d'immunoblot devient IRIS — Interprétation et Reconnaissance des Immunoblots Sérologiques. Même outil, mêmes fonctions : seul le nom change, pour l'identifier plus facilement aux côtés d'ALMA.",
      },
      {
        icon: LuTarget,
        title: 'Interface rafraîchie',
        body: "Barre latérale et en-têtes redessinés (style néon-glass, couleurs harmonisées). Les modules gardent leur couleur d'identité (IRIS en teal).",
      },
    ],
  },
  {
    version: '2026-07-26',
    date: '26 juillet 2026',
    items: [
      {
        icon: LuSparkles,
        title: 'Suggestion des bandes pour tous les tests',
        body: "La suggestion automatique de position des bandes couvre désormais les 6 tests : Toxo IgG et IgM, Toxocara, Schisto, Aspergillus et Échinococcus. Placez le témoin, les bandes de référence se calent toutes seules.",
      },
      {
        icon: LuTarget,
        title: 'Guide du témoin enrichi',
        body: "Le guide affiché sur le témoin positif montre maintenant le diagnostic d'espèce (Échinococcus), les bandes intermédiaires, le contrôle qualité et les limitations — dans une mise en page compacte en colonnes.",
      },
      {
        icon: LuFileText,
        title: "Bouton d'export réactif",
        body: "L'export s'active dès que toutes les bandelettes sont validées, sans avoir à rafraîchir la page.",
      },
      {
        icon: LuActivity,
        title: 'Compteurs des cas corrigés',
        body: "Les cartes des cas affichent le bon nombre de blots, de positifs et de négatifs après validation (au lieu de rester à 0).",
      },
    ],
  },
  {
    version: '2026-07-23',
    date: '23 juillet 2026',
    items: [
      {
        icon: LuMove,
        title: 'Positionnement au pixel près',
        body: "Recaler une bandelette de 1 ou 2 px fonctionne enfin — plus de retour à la position d'origine. Maintenez Maj pendant le déplacement, la rotation ou le redimensionnement pour un réglage fin (5× plus lent).",
      },
      {
        icon: LuRotateCw,
        title: 'Rotation redressée facilement',
        body: 'Les petites rotations de quelques degrés sont désormais possibles et stables. Maj ralentit le geste pour viser au dixième de degré.',
      },
      {
        icon: LuTarget,
        title: 'Bandes suggérées bien mieux placées',
        body: "La détection de la bande P30 (l'ancre du calage) a été fiabilisée, en particulier sur les images scannées. Les bandes proposées tombent au bon endroit.",
      },
      {
        icon: LuSparkles,
        title: 'Suggestion Toxo IgM',
        body: 'Le module reconnaît maintenant le motif de bandes du Toxo IgM (P40, P38, P33, P31) et les suggère automatiquement, comme pour le Toxo IgG.',
      },
      {
        icon: LuActivity,
        title: 'Aide au choix du témoin positif',
        body: "Le module ne présélectionne plus le témoin en silence. Il met en avant la ou les pistes qui ressemblent le plus à un témoin (badge « Témoin ? ») ; vous confirmez d'un clic. C'est vous qui décidez.",
      },
      {
        icon: LuFileText,
        title: 'Nom du fichier visible',
        body: "Le nom du scan s'affiche à côté de « Image Source », même quand l'image est repliée : on sait de quel fichier vient le cas d'un coup d'œil.",
      },
    ],
  },
]

// Date LOCALE du poste (pas UTC : les lecteurs sont en France, une entrée du
// lundi ne doit pas paraître dimanche à 23 h ni attendre lundi 1 h du matin).
function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
/** Les entrées effectivement publiées — celles dont la date est atteinte.
 *  En développement, les entrées à date future s'affichent aussi : c'est le
 *  seul moyen de relire une nouveauté déposée d'avance avant son jour J. */
function visibleReleases(): Release[] {
  if (process.env.NODE_ENV === 'development') return RELEASES
  return RELEASES.filter((r) => r.version <= todayISO())
}
/** Latest PUBLISHED version — what "seen" is measured against. */
function latestVersion(): string {
  return visibleReleases()[0]?.version ?? ''
}
const STORAGE_KEY = 'careflai.blotWhatsNew.lastSeen'

function readLastSeen(): string | null {
  try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
}
/** True once the device has acknowledged the most recent published release. */
export function hasSeenBlotWhatsNew(): boolean {
  return readLastSeen() === latestVersion()
}
export function markBlotWhatsNewSeen() {
  try { localStorage.setItem(STORAGE_KEY, latestVersion()) } catch { /* ignore */ }
}

export default function BlotWhatsNew({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  // Snapshot lastSeen when the modal opens, so the "Nouveau" flags stay stable
  // while it's on screen (we mark it seen on close, which would otherwise clear
  // them mid-view). Versions are ISO dates → lexical compare is chronological.
  const [seenAtOpen, setSeenAtOpen] = useState<string | null>(null)
  useEffect(() => { if (isOpen) setSeenAtOpen(readLastSeen()) }, [isOpen])

  const handleClose = () => {
    markBlotWhatsNewSeen()
    onClose()
  }
  const isNew = (version: string) => !seenAtOpen || version > seenAtOpen
  const releases = visibleReleases()

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader p={0}>
          <Box bgGradient="linear(135deg, teal.500, cyan.500)" color="white" px={6} py={5}>
            <HStack spacing={3} align="center">
              <Box bg="whiteAlpha.300" borderRadius="xl" p={2.5}>
                <Icon as={LuGift} boxSize={6} />
              </Box>
              <Box>
                <HStack spacing={2}>
                  <Text fontSize="lg" fontWeight="bold" lineHeight="1.2">Nouveautés d'IRIS</Text>
                  <Badge colorScheme="orange" variant="solid" fontSize="2xs" borderRadius="md">
                    {releases[0]?.version ?? ''}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="whiteAlpha.900" fontWeight="normal">
                  IRIS, analyse d'immunoblot — historique des améliorations
                </Text>
              </Box>
            </HStack>
          </Box>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} right={4} />

        <ModalBody px={6} py={5}>
          <VStack align="stretch" spacing={6}>
            {releases.map((release, ri) => (
              <Box key={release.version}>
                {ri > 0 && <Divider mb={4} />}
                <HStack spacing={2} mb={3}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                    {release.date}
                  </Text>
                  {isNew(release.version) && (
                    <Badge colorScheme="green" variant="solid" fontSize="2xs" borderRadius="md">
                      Nouveau
                    </Badge>
                  )}
                </HStack>
                <VStack align="stretch" spacing={4}>
                  {release.items.map((c) => (
                    <HStack key={c.title} align="flex-start" spacing={3.5}>
                      <Box bg="teal.50" color="teal.600" borderRadius="lg" p={2} mt={0.5} flexShrink={0}>
                        <Icon as={c.icon} boxSize={5} />
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" color="gray.800" fontSize="sm">{c.title}</Text>
                        <Text color="gray.600" fontSize="sm" lineHeight="1.5">{c.body}</Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100">
          <Text fontSize="xs" color="gray.400" mr="auto">
            Rouvrable à tout moment via le badge « UPDATED » du bandeau.
          </Text>
          <Button colorScheme="teal" onClick={handleClose} borderRadius="lg">
            J'ai compris
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
